import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CHECKOUT } from "@/constants/testIds";
import { ShieldCheck, MapPin, AlertTriangle, Ticket, CheckCircle2, X, ExternalLink } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

export default function CheckoutPage() {
  const { items, totals, pincode, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    line1: "",
    line2: "",
    city: "",
    state: "Telangana",
    pincode: pincode || "",
    landmark: "",
    lat: null,
    lng: null,
  });
  const [error, setError] = useState("");
  const [pincodeCheck, setPincodeCheck] = useState({ status: "empty", message: "Enter a pincode to check delivery availability." });
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // { code, discount, description }
  const [couponError, setCouponError] = useState("");
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [saved, setSaved] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);

  useEffect(() => {
    const pincodeValue = form.pincode.trim();
    if (!pincodeValue) {
      setPincodeCheck({ status: "empty", message: "Enter a pincode to check delivery availability." });
      return;
    }
    if (!/^\d{6}$/.test(pincodeValue)) {
      setPincodeCheck({ status: "invalid", message: "Enter a valid 6-digit pincode." });
      return;
    }

    let current = true;
    setPincodeCheck({ status: "checking", message: "Checking delivery availability..." });
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/pincode/check", { params: { pincode: pincodeValue } });
        if (current) {
          setPincodeCheck(data.serviceable
            ? { status: "available", message: "Delivery is available to this pincode." }
            : { status: "unavailable", message: "Delivery is currently available only in Hyderabad." });
        }
      } catch {
        if (current) setPincodeCheck({ status: "error", message: "Unable to check this pincode. Please try again." });
      }
    }, 250);

    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [form.pincode]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/addresses").then((r) => {
      const list = r.data.addresses || [];
      setSaved(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) selectAddress(def);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selectAddress = (a) => {
    setSelectedAddrId(a.id);
    setForm((f) => ({
      ...f,
      name: a.name, phone: a.phone, line1: a.line1, line2: a.line2 || "",
      city: a.city, state: a.state, pincode: a.pincode, landmark: a.landmark || "",
      lat: a.lat ?? null, lng: a.lng ?? null,
    }));
  };
  const useNewAddress = () => {
    setSelectedAddrId(null);
    setForm((f) => ({ ...f, line1: "", line2: "", city: "", pincode: pincode || "", landmark: "", lat: null, lng: null }));
  };

  const applyCoupon = async () => {
    setCouponError("");
    if (!couponCode.trim()) return;
    setCheckingCoupon(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.trim(),
        subtotal: totals.subtotal,
      });
      setCouponApplied(data);
      toast.success(`Coupon applied — you save ${inr(data.discount)}`);
    } catch (err) {
      setCouponApplied(null);
      setCouponError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCheckingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode("");
    setCouponError("");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!totals.minimumOrderMet) {
      setError(`Minimum order value ${inr(totals.minimumOrderValue)} required.`);
      return;
    }
    if (pincodeCheck.status !== "available") {
      setError(pincodeCheck.status === "empty"
        ? "Enter your pincode to check delivery availability."
        : pincodeCheck.status === "unavailable"
          ? "Delivery is currently available only in Hyderabad."
          : pincodeCheck.message);
      return;
    }
    if (items.length === 0) {
      setError("Your cart is empty.");
      return;
    }
    setSubmitting(true);
    try {
      const availability = await api.get("/pincode/check", { params: { pincode: form.pincode.trim() } });
      if (!availability.data.serviceable) {
        setPincodeCheck({ status: "unavailable", message: "Delivery is currently available only in Hyderabad." });
        setError("Delivery is currently available only in Hyderabad.");
        return;
      }
      const payload = {
        items: items.map((i) => ({
          kind: i.kind,
          ref_id: i.ref_id,
          name: i.name,
          unit_price: i.unit_price,
          qty: i.qty,
          image_url: i.image_url,
        })),
        address: {
          name: form.name,
          phone: form.phone,
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          landmark: form.landmark,
          lat: form.lat ?? null,
          lng: form.lng ?? null,
        },
        payment_method: "COD",
        notes: form.notes || "",
        coupon_code: couponApplied?.code || null,
        delivery_slot: null,
        save_address: !selectedAddrId,
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_number} placed!`);
      clear();
      navigate(`/orders/${data.id}`, { state: { fromCheckout: true } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <p className="text-hyd-text2">Cart is empty.</p>
        <Button onClick={() => navigate("/")} className="mt-4 bg-hyd-gold text-hyd-bg">Continue shopping</Button>
      </div>
    );
  }

  return (
    <div data-testid={CHECKOUT.page} className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="font-display text-3xl font-extrabold">Checkout</h1>
      <div className="mt-5 grid md:grid-cols-3 gap-5">
        <form onSubmit={submit} className="md:col-span-2 bg-hyd-panel border border-hyd-border rounded-xl p-5 space-y-4">
          {saved.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-hyd-gold font-bold uppercase text-xs tracking-widest mb-2">
                <MapPin className="w-4 h-4" /> Choose a saved address
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {saved.map((a) => {
                  const active = selectedAddrId === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`checkout-address-${a.id}`}
                      onClick={() => selectAddress(a)}
                      className={`text-left p-3 rounded-2xl border transition-colors ${active ? "border-hyd-gold bg-hyd-gold/10 shadow-glow-gold" : "border-hyd-border bg-hyd-bg hover:border-hyd-gold/60"}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase font-bold tracking-widest text-hyd-gold">{a.label}</span>
                        {a.is_default && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-hyd-gold/20 text-hyd-gold">Default</span>}
                      </div>
                      <div className="text-sm font-semibold">{a.name}</div>
                      <div className="text-xs text-hyd-text2 line-clamp-2">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} - {a.pincode}</div>
                      <div className="text-xs text-hyd-text3 mt-0.5">📞 {a.phone}</div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  data-testid="checkout-use-new-address"
                  onClick={useNewAddress}
                  className={`text-center p-3 rounded-2xl border-2 border-dashed transition-colors ${selectedAddrId == null ? "border-hyd-gold bg-hyd-gold/5 text-hyd-gold" : "border-hyd-border text-hyd-text2 hover:border-hyd-gold/60"}`}
                >
                  <div className="text-xs font-bold uppercase tracking-widest">+ New address</div>
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 text-hyd-gold font-bold uppercase text-xs tracking-widest">
            <MapPin className="w-4 h-4" /> {selectedAddrId ? "Deliver to" : "Delivery address"}
          </div>

          {!selectedAddrId && (
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block">Search your area / building</Label>
              <AddressAutocomplete
                placeholder="e.g. Symphony Park Homes, Beeramguda"
                onPick={({ line1, line2, city, state, pincode, lat, lng }) => {
                  setForm((f) => ({
                    ...f,
                    line1: line1 || f.line1,
                    line2: line2 || f.line2,
                    city: city || f.city,
                    state: state || f.state,
                    pincode: pincode || f.pincode,
                    lat: lat ?? null,
                    lng: lng ?? null,
                  }));
                  toast.success("Address auto-filled — please add house/flat number");
                }}
              />
              {form.lat && form.lng && (
                <div className="mt-1 flex items-center gap-2 text-[11px] text-hyd-text3">
                  <MapPin className="w-3 h-3 text-hyd-gold" />
                  <span>Pinned: {form.lat.toFixed(5)}, {form.lng.toFixed(5)}</span>
                  <a href={`https://www.google.com/maps?q=${form.lat},${form.lng}`} target="_blank" rel="noreferrer" className="text-hyd-gold hover:underline inline-flex items-center gap-0.5">
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name">
              <Input data-testid={CHECKOUT.nameInput} value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </Field>
            <Field label="Phone">
              <Input data-testid={CHECKOUT.phoneInput} value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
            </Field>
            <Field label="Address line 1" full>
              <Input data-testid={CHECKOUT.line1Input} value={form.line1} onChange={(e) => set("line1", e.target.value)} required />
            </Field>
            <Field label="Address line 2" full>
              <Input data-testid={CHECKOUT.line2Input} value={form.line2} onChange={(e) => set("line2", e.target.value)} />
            </Field>
            <Field label="City">
              <Input data-testid={CHECKOUT.cityInput} value={form.city} onChange={(e) => set("city", e.target.value)} required />
            </Field>
            <Field label="State">
              <Input data-testid={CHECKOUT.stateInput} value={form.state} onChange={(e) => set("state", e.target.value)} required />
            </Field>
            <Field label="Pincode">
              <>
                <Input data-testid={CHECKOUT.pincodeInput} value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} required maxLength={6} inputMode="numeric" />
                <div role="status" className={`mt-1 text-xs ${pincodeCheck.status === "available" ? "text-emerald-400" : pincodeCheck.status === "unavailable" || pincodeCheck.status === "invalid" ? "text-hyd-red" : "text-hyd-text3"}`}>
                  {pincodeCheck.message}
                </div>
              </>
            </Field>
            <Field label="Landmark">
              <Input value={form.landmark} onChange={(e) => set("landmark", e.target.value)} />
            </Field>
          </div>

          <div className="border-t border-hyd-border pt-4">
            <div className="text-hyd-gold font-bold uppercase text-xs tracking-widest mb-2 flex items-center gap-2">
              <Ticket className="w-4 h-4" /> Coupon code
            </div>
            {couponApplied ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-950/40 border border-emerald-800 text-emerald-200 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <div>
                    <span className="font-mono font-bold text-emerald-300">{couponApplied.code}</span> applied — save {inr(couponApplied.discount)}
                    {couponApplied.description && <div className="text-xs text-emerald-200/80">{couponApplied.description}</div>}
                  </div>
                </div>
                <button data-testid={CHECKOUT.couponRemoveBtn} type="button" onClick={removeCoupon} className="text-emerald-300 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  data-testid={CHECKOUT.couponInput}
                  placeholder="Enter code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="bg-hyd-bg border-hyd-border text-hyd-text font-mono tracking-wider uppercase"
                />
                <Button data-testid={CHECKOUT.couponApplyBtn} type="button" onClick={applyCoupon} disabled={checkingCoupon || !couponCode.trim()} variant="secondary" className="bg-hyd-panel2 border border-hyd-border text-hyd-gold">
                  {checkingCoupon ? "..." : "Apply"}
                </Button>
              </div>
            )}
            {couponError && <div className="text-hyd-red text-xs mt-1">{couponError}</div>}
          </div>

          <div className="border-t border-hyd-border pt-4">
            <div className="text-hyd-gold font-bold uppercase text-xs tracking-widest mb-2">Payment</div>
            <div className="p-3 rounded-lg bg-hyd-bg border border-hyd-border text-sm text-hyd-text2">
              Pay On Delivery via cash or UPI.<br />
              Card payment not accepted.
            </div>
          </div>

          <div className="border-t border-hyd-border pt-4">
            <Label className="text-xs text-hyd-text2 mb-1 block">Comments / delivery notes (optional)</Label>
            <textarea
              data-testid="checkout-notes"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              placeholder="Ring the doorbell, leave at gate, etc."
              className="w-full bg-hyd-bg border border-hyd-border text-hyd-text rounded-md p-2 text-sm placeholder:text-hyd-text3"
            />
          </div>

          {error && (
            <div className="p-3 rounded-md bg-hyd-red/10 border border-hyd-red/30 text-hyd-red text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <p role="note" className="text-xs text-hyd-text3 text-center">
            Delivery is currently available only in Hyderabad.
          </p>
          {!totals.minimumOrderMet && (
            <p role="status" className="text-sm text-hyd-red text-center">
              Minimum order value {inr(totals.minimumOrderValue)}. Add {inr(totals.minimumOrderRemaining)} to place your order.
            </p>
          )}

          <Button
            data-testid={CHECKOUT.placeOrderBtn}
            type="submit"
            disabled={submitting || pincodeCheck.status !== "available" || !totals.minimumOrderMet}
            className="w-full bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider text-sm h-11"
          >
            {submitting ? "Placing order..." : `Place order · ${inr(totals.total - (couponApplied?.discount || 0))}`}
          </Button>
        </form>

        <aside className="bg-hyd-panel border border-hyd-border rounded-xl p-4 h-fit md:sticky md:top-20">
          <h3 className="font-display text-lg font-bold mb-3">Summary</h3>
          <div className="space-y-2 text-sm max-h-52 overflow-auto pr-1">
            {items.map((i) => (
              <div key={i.key} className="flex justify-between gap-2">
                <span className="text-hyd-text2 line-clamp-1">{i.name} × {i.qty}</span>
                <span>{inr(i.unit_price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-hyd-border my-3" />
          <Row label="Subtotal" value={inr(totals.subtotal)} />
          <Row label="Delivery" value={totals.deliveryFee === 0 ? "FREE" : inr(totals.deliveryFee)} />
          {couponApplied && (
            <Row label={`Coupon (${couponApplied.code})`} value={`- ${inr(couponApplied.discount)}`} />
          )}
          <div className="border-t border-hyd-border my-3" />
          <Row label="Total" value={inr(totals.total - (couponApplied?.discount || 0))} bold />
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-hyd-text2">{label}</span>
      <span>{value}</span>
    </div>
  );
}
